import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import flattenMessages from 'i18n/flattenMessages';
import type { Locale } from 'i18n/localePath';
import { withLocalePrefix } from 'i18n/localePath';
import en from 'i18n/messages/en';
import nl from 'i18n/messages/nl';
import trackEvent from 'utils/trackEvent';

const enFlat = flattenMessages(en as unknown as Record<string, unknown>);
const nlFlat = flattenMessages(nl as unknown as Record<string, unknown>);

export type TFunction = (key: string, vars?: Record<string, string>) => string;

export interface I18nContextValue {
  locale: Locale;
  strippedPath: string;
  t: TFunction;
  localizePath: (path: string) => string;
  switchLocale: (next: Locale) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function interpolate(template: string, vars?: Record<string, string>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    vars[name] !== undefined ? vars[name] : `{${name}}`
  );
}

export function I18nProvider({
  locale,
  strippedPath,
  history,
  children,
}: {
  locale: Locale;
  strippedPath: string;
  history: RouteComponentProps['history'];
  children: React.ReactNode;
}) {
  const messages = locale === 'nl' ? nlFlat : enFlat;

  const t = useCallback<TFunction>(
    (key, vars) => interpolate(messages[key] ?? key, vars),
    [messages]
  );

  const localizePath = useCallback((path: string) => withLocalePrefix(locale, path), [locale]);

  const switchLocale = useCallback(
    (next: Locale) => {
      if (next === locale) return;
      trackEvent('locale_switch', { from: locale, to: next, path: strippedPath });
      history.push(withLocalePrefix(next, strippedPath));
    },
    [history, locale, strippedPath]
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      strippedPath,
      t,
      localizePath,
      switchLocale,
    }),
    [locale, strippedPath, t, localizePath, switchLocale]
  );

  useEffect(() => {
    document.documentElement.lang = locale === 'nl' ? 'nl' : 'en';
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
}



// WEBPACK FOOTER //
// ./src/i18n/I18nContext.tsx